import { intersection } from "es-toolkit";

import type { NodeRole } from "@skilitics/nautilus-core";
import type { GetNodeSlotId, GetNodeTypeIdById } from "@skilitics/nautilus-core-designer-data";

import type { DesignerNode, GetManifestByTypeId } from "../types/index.ts";

interface Options {
  getManifest: GetManifestByTypeId;
  getNodeTypeId?: GetNodeTypeIdById;
  getSlotId?: GetNodeSlotId;
}

export const getNodeRoleCreate =
  ({ getManifest, getSlotId, getNodeTypeId }: Options) =>
  ({
    id,
    typeId,
    role,
  }: Pick<DesignerNode, "typeId" | "role"> & Partial<Pick<DesignerNode, "id">>): NodeRole | undefined => {
    if (role) {
      return role;
    }

    const {
      terminus: { roles },
    } = getManifest(typeId);

    // determine from the manifest of the parent which role the node is playing
    if (roles && roles.length > 1 && id) {
      if (!(getSlotId && getNodeTypeId)) {
        throw new Error(
          `Node ${typeId} has multiple roles but getNodeRole not given what it needs to determine which.`,
        );
      }
      const slotId = getSlotId(id);
      if (slotId) {
        const parentTypeId = getNodeTypeId(slotId.parentId);
        const {
          terminus: { children: slots },
        } = getManifest(parentTypeId);
        const slotDefinition = slots?.[slotId.name];
        if (slotDefinition) {
          if ("role" in slotDefinition) {
            return slotDefinition.role;
          }
          if ("roles" in slotDefinition) {
            const sharedRoles = intersection(
              slotDefinition.roles,
              roles.filter((r) => !!r),
            );
            if (sharedRoles.length > 0) {
              // pick first one
              return sharedRoles[0];
            }
          }
        }
      }
    }

    return roles?.[0]; // first is default
  };

export type GetNodeRole = ReturnType<typeof getNodeRoleCreate>;
